import { useEffect, useState } from 'react';
import styled from 'styled-components';
import _ from 'lodash';
import { Flex } from './base/layout-components';
import SearchableList from './SearchableList';
import Chip from './base/ChipV2';
import Text from './base/Text';
import { pluralize } from '../common/utils/utils';
import SearchInput from './SearchInput';
import Tooltip from '@material-ui/core/Tooltip';
import { LBButton, LBXIcon } from '@linearb/buzz';

const StyledListItem = styled(Flex)`
  ${({ disabled }) =>
    disabled
      ? `
    opacity: 0.6;
    pointer-events: none;
`
      : ''}
`;

const ItemSearchKeysToString = (searchKeys, item) =>
  searchKeys.reduce((acc, key) => acc + item[key], '');
const SelectedChip = ({
  children,
  onClose,
  icon = 'roundedClose',
  iconSize = 'smaller',
  style,
  tooltipText = ''
}) => (
  <Chip style={style} tooltipText={tooltipText}>
    <Flex items='center' justify='center'>
      {children}
      <Tooltip title={tooltipText} disableHoverListener={!tooltipText}>
        <span className='ml-1'>
          <LBButton variant='ghost' size='icon' onClick={onClose}>
            <LBXIcon name={icon} />
          </LBButton>
        </span>
      </Tooltip>
    </Flex>
  </Chip>
);
const SelectedListTitle = ({
  showSearchSelected,
  selectedAmount,
  itemName,
  showCount,
  searchValue,
  onSearch,
  placeholder
}) => (
  <Flex items='center' mb='normal'>
    {showCount && (
      <Text
        minWidth='fit-content'
        lineHeight='1.6rem'
        color='shuttleGray'
        size='normal'
        weight='400'
      >
        {pluralize(selectedAmount, itemName)}
      </Text>
    )}
    {(showCount || showSearchSelected) && (
      <Flex
        backgroundColor='white'
        ml={showCount && 'smaller'}
        width='28rem'
        visibility={showSearchSelected ? null : 'hidden'}
      >
        <SearchInput
          value={searchValue}
          onChange={onSearch}
          placeholder={placeholder}
          height='2.5rem'
        />
      </Flex>
    )}
  </Flex>
);

/**
 * Filters a list of selected items based on a given input and a set of search keys.
 * Only items that contain the input string in any of the specified search keys will be included in the result.
 * @param {Array} selectedItems - The array of items to be filtered.
 * @param {string} input - The input string to be used for filtering.
 * @param {Array} searchKeys - An array of keys to be searched in each item.
 * @returns {Array} An array of filtered items.
 */
export const filterSelectedItems = (selectedItems, input, searchKeys) =>
  input
    ? selectedItems.filter(
        item =>
          !!searchKeys
            .map(id =>
              item[id]
                ?.toString()
                .toLowerCase()
                .includes(input.toString().toLowerCase())
            )
            .filter(Boolean).length
      )
    : [];

const SearchableListWithChips = ({
  itemName = 'item',
  placeholder,
  items,
  searchKeys = [],
  chip: Chip,
  emptyState,
  onSelectItem = _.noop,
  onRemoveItem = _.noop,
  showSearchOnCount = 20,
  selectedItems = [],
  showCount = false,
  height = '26rem',
  listWidth = '29rem',
  chipsSearchPlaceholder = placeholder
}) => {
  const [listItems, setListItems] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [displayedSelectedItems, setDisplayedSelectedItems] = useState([]);
  const onSearch = input => {
    setSearchValue(input);
    const filteredItems = filterSelectedItems(selectedItems, input, searchKeys);
    setDisplayedSelectedItems(filteredItems);
  };
  const onItemClick = item => () => {
    onSelectItem(item);
  };
  const onXChipClick = clickedItem => () => {
    const filterFunc = item =>
      ItemSearchKeysToString(searchKeys, item) !==
      ItemSearchKeysToString(searchKeys, clickedItem);
    onRemoveItem(clickedItem);
    setDisplayedSelectedItems(displayedSelectedItems.filter(filterFunc));
  };
  useEffect(() => {
    const tempItems = items.map((item, index) => ({
      ...item,
      view: (
        <StyledListItem
          key={item[searchKeys[0]] + index}
          onClick={onItemClick(item)}
          disabled={item.isDisabled}
        >
          {item.view}
        </StyledListItem>
      )
    }));
    _.remove(tempItems, selectedItem =>
      selectedItems
        .map(item => ItemSearchKeysToString(searchKeys, item))
        .includes(ItemSearchKeysToString(searchKeys, selectedItem))
    );
    setListItems(tempItems);
  }, [selectedItems]);
  const showSearchSelected =
    showSearchOnCount <= selectedItems.length || searchValue;
  const selectedItemsToDisplay =
    displayedSelectedItems.length || searchValue
      ? displayedSelectedItems
      : selectedItems;
  return (
    <Flex height={height} maxHeight={height} width='100%'>
      <Flex
        inline
        minWidth={listWidth}
        items='center'
        backgroundColor='white'
        mr='medium'
      >
        <SearchableList
          placeholder={placeholder}
          searchKeys={searchKeys}
          emptyState={emptyState}
          items={listItems}
        />
      </Flex>
      <Flex direction='column' width='100%'>
        <SelectedListTitle
          itemName={itemName}
          placeholder={chipsSearchPlaceholder}
          selectedAmount={selectedItems.length}
          showCount={showCount}
          searchValue={searchValue}
          onSearch={onSearch}
          showSearchSelected={showSearchSelected}
        />
        <Flex overflowY='hidden' overFlowX='scroll'>
          <Flex direction='column' height='100%' wrap='wrap' gap='1rem'>
            {selectedItemsToDisplay.map((item, index) => (
              <SelectedChip
                key={item[searchKeys[0]] + index}
                onClose={onXChipClick(item)}
                {...(item.chipProps || {})}
              >
                <Chip {...item} />
              </SelectedChip>
            ))}
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};
export default SearchableListWithChips;
